import React, { useState, useEffect, useRef } from 'react';
import {
  CloudArrowUpIcon,
  DocumentArrowUpIcon,
  UsersIcon,
  ChartBarIcon,
  PresentationChartLineIcon,
  ChatBubbleLeftRightIcon,
} from '@heroicons/react/24/outline';
import { Link } from 'react-router-dom';
import OCRApiService, { api } from '../services/api';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';
import { isFeatureEnabled } from '../config/accountSettings';
import AccountManagement from '../components/AccountManagement';
import UsageBreakdownModal from '../components/UsageBreakdownModal';

interface User {
  id: number;
  username: string;
  email: string;
  full_name: string | null;
  is_admin: boolean;
  is_super_admin: boolean;
  is_active: boolean;
  requires_approval: boolean;
  can_complete_po_line: boolean;
  default_complete_po_yes: boolean;
  customer_group: string | null;
  account_id: number | null;
  account_name: string | null;
  created_at: string;
}

interface AssignedUser {
  id: number;
  username: string;
  email: string;
  assigned_at: string | null;
  created_count: number;
  updated_count: number;
  total_processed: number;
}

interface VendorImport {
  id: number;
  filename: string;
  file_path: string;
  file_size: number;
  file_type: string | null;  // 'vendor_po' or 'vendor_number'
  assigned_users: AssignedUser[];  // legacy — superseded by account_id
  customer_group: string | null;
  account_id: number | null;
  account_name: string | null;
  imported_by: {
    id: number;
    username: string;
  } | null;
  created_at: string;
}

const AdminPanel: React.FC = () => {
  const { user: currentAdmin } = useAuth();
  const isSuperAdmin = !!currentAdmin?.is_super_admin;

  // Vendor Files State
  const [vendorFiles, setVendorFiles] = useState<VendorImport[]>([]);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [selectedFileType, setSelectedFileType] = useState<string>('vendor_number'); // Default to vendor_number
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [isDeletingFile, setIsDeletingFile] = useState<number | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // User Management State
  const [users, setUsers] = useState<User[]>([]);
  const [isLoadingUsers, setIsLoadingUsers] = useState<boolean>(false);
  const [isDeletingUser, setIsDeletingUser] = useState<number | null>(null);
  const [showDeleteUserConfirm, setShowDeleteUserConfirm] = useState<number | null>(null);
  const [usageUser, setUsageUser] = useState<{ id: number; label: string } | null>(null);

  // Assignment State

  useEffect(() => {
    loadVendorFiles();
    loadUsers();
  }, []);

  // Red-dot indicator on the Customer Feedback button when a reporter
  // has replied on a thread since the last time this admin opened the
  // panel. Mirrors the reporter-side dot on the floating button.
  const [hasNewReporterReply, setHasNewReporterReply] = useState(false);
  const ADMIN_FEEDBACK_LAST_SEEN_KEY = 'keyscan.adminfeedback.lastSeenAt';
  useEffect(() => {
    if (!isSuperAdmin) return;
    let cancelled = false;
    const check = async () => {
      try {
        const res = await api.get('/admin/feedback/latest-reporter-reply');
        const iso: string | null = res.data?.latest_at || null;
        if (!iso) {
          if (!cancelled) setHasNewReporterReply(false);
          return;
        }
        let seen = 0;
        try {
          seen = Number(localStorage.getItem(ADMIN_FEEDBACK_LAST_SEEN_KEY) || 0) || 0;
        } catch {
          seen = 0;
        }
        if (!cancelled) setHasNewReporterReply(new Date(iso).getTime() > seen);
      } catch {
        if (!cancelled) setHasNewReporterReply(false);
      }
    };
    check();
    const onFocus = () => check();
    window.addEventListener('focus', onFocus);
    return () => {
      cancelled = true;
      window.removeEventListener('focus', onFocus);
    };
  }, [isSuperAdmin]);

  const markFeedbackSeen = () => {
    try {
      localStorage.setItem(ADMIN_FEEDBACK_LAST_SEEN_KEY, String(Date.now()));
    } catch {
      // localStorage unavailable; dot will clear on the next backend check
      // when there is no reply newer than the current admin activity.
    }
    setHasNewReporterReply(false);
  };

  // ============================================================================
  // VENDOR FILES FUNCTIONS
  // ============================================================================

  const loadVendorFiles = async () => {
    try {
      const response = await OCRApiService.getVendorImports();
      if (response.success && response.imports) {
        setVendorFiles(response.imports);
      }
    } catch (error: any) {
      console.error('Failed to load vendor files:', error);
      toast.error(error.response?.data?.detail || 'Failed to load vendor files');
    }
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (!file.name.endsWith('.csv')) {
        toast.error('Please select a CSV file');
        return;
      }
      setSelectedFile(file);
    }
  };

  const handleUploadFile = async () => {
    if (!selectedFile) {
      toast.error('Please select a CSV file to upload');
      return;
    }

    if (!selectedFileType) {
      toast.error('Please select a file type');
      return;
    }

    try {
      setIsUploading(true);
      // Upload with file_type - can assign users later
      const response = await OCRApiService.importVendorsCSV(selectedFile, selectedFileType, undefined, undefined);
      if (response.success) {
        toast.success('Vendor file uploaded successfully');
        setSelectedFile(null);
        setSelectedFileType('vendor_number'); // Reset to default
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        await loadVendorFiles();
      }
    } catch (error: any) {
      console.error('Failed to upload vendor file:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload vendor file');
    } finally {
      setIsUploading(false);
    }
  };

  const handleDeleteFile = async (fileId: number) => {
    try {
      setIsDeletingFile(fileId);
      const response = await OCRApiService.deleteVendorImport(fileId);
      if (response.success) {
        toast.success('Vendor file deleted successfully');
        await loadVendorFiles();
      }
    } catch (error: any) {
      console.error('Failed to delete vendor file:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete vendor file');
    } finally {
      setIsDeletingFile(null);
      setShowDeleteUserConfirm(null);
    }
  };

  // ============================================================================
  // USER MANAGEMENT FUNCTIONS
  // ============================================================================

  const loadUsers = async () => {
    try {
      setIsLoadingUsers(true);
      const response = await OCRApiService.getUsers();
      if (response.success && response.users) {
        setUsers(response.users);
      }
    } catch (error: any) {
      console.error('Failed to load users:', error);
      toast.error(error.response?.data?.detail || 'Failed to load users');
    } finally {
      setIsLoadingUsers(false);
    }
  };

  const handleDeleteUser = async (userId: number) => {
    try {
      setIsDeletingUser(userId);
      const response = await OCRApiService.deleteUser(userId);
      if (response.success) {
        toast.success('User deleted successfully');
        await loadUsers();
        await loadVendorFiles(); // Refresh to update assignments
      }
    } catch (error: any) {
      console.error('Failed to delete user:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    } finally {
      setIsDeletingUser(null);
      setShowDeleteUserConfirm(null);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8 flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Admin Panel</h1>
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">Manage vendor files, users, and assignments</p>
          </div>
          {isSuperAdmin && (
            <div className="flex flex-wrap items-center gap-2">
              <Link
                to="/admin/feedback"
                onClick={markFeedbackSeen}
                className="relative inline-flex items-center gap-2 px-4 py-2.5 bg-brand-600 hover:bg-brand-700 text-white text-sm font-semibold rounded-lg shadow-sm transition-colors"
              >
                <ChatBubbleLeftRightIcon className="h-5 w-5" />
                Customer Feedback
                {hasNewReporterReply && (
                  <span
                    aria-label="New reply from a reporter"
                    title="New reply from a reporter"
                    className="absolute -top-1 -right-1 h-3.5 w-3.5 rounded-full bg-red-500 border-2 border-white dark:border-gray-900 animate-pulse"
                  />
                )}
              </Link>
              <Link
                to="/admin/dashboard"
                className="inline-flex items-center gap-2 px-4 py-2.5 bg-brand-600 hover:bg-brand-700 text-white text-sm font-semibold rounded-lg shadow-sm transition-colors"
              >
                <PresentationChartLineIcon className="h-5 w-5" />
                Executive Dashboard
              </Link>
            </div>
          )}
        </div>

        {isSuperAdmin && <AccountManagement />}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* VENDOR FILES SECTION */}
          <div className="bg-white dark:bg-gray-800 shadow rounded-lg">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
                <DocumentArrowUpIcon className="h-5 w-5 mr-2" />
                Vendor Files
              </h2>
            </div>
            <div className="p-6 space-y-4">
              {/* Upload Section */}
              <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-4">
                <input
                  type="file"
                  accept=".csv"
                  onChange={handleFileSelect}
                  ref={fileInputRef}
                  className="hidden"
                  id="vendor-file-upload"
                />
                <label
                  htmlFor="vendor-file-upload"
                  className="flex flex-col items-center cursor-pointer"
                >
                  <CloudArrowUpIcon className="h-8 w-8 text-gray-400 dark:text-gray-500 mb-2" />
                  <span className="text-sm text-gray-600 dark:text-gray-400">
                    {selectedFile ? selectedFile.name : 'Click to select CSV file'}
                  </span>
                </label>
                {selectedFile && (
                  <>
                    <div className="mt-4">
                      <label htmlFor="file-type" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        File Type
                      </label>
                      <select
                        id="file-type"
                        value={selectedFileType}
                        onChange={(e) => setSelectedFileType(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-brand-500 focus:border-brand-500"
                      >
                        <option value="vendor_number">Vendor Number File</option>
                        <option value="vendor_po">Vendor PO File</option>
                      </select>
                      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                        {selectedFileType === 'vendor_po'
                          ? 'Used for PO lookup'
                          : 'Used for vendor lookup'}
                      </p>
                    </div>
                    <button
                      onClick={handleUploadFile}
                      disabled={isUploading}
                      className="mt-4 w-full px-4 py-2 bg-brand-600 text-white rounded-md hover:bg-brand-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
                    >
                      {isUploading ? 'Uploading...' : 'Upload File'}
                    </button>
                  </>
                )}
              </div>

              {/* Files List */}
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {vendorFiles.length === 0 ? (
                  <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-4">No vendor files uploaded yet</p>
                ) : (
                  vendorFiles.map((file) => (
                    <div
                      key={file.id}
                      className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-200 dark:border-gray-600"
                    >
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{file.filename}</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          {OCRApiService.formatFileSize(file.file_size)} • {new Date(file.created_at).toLocaleDateString()}
                          {file.file_type && (
                            <span className="ml-2 px-2 py-0.5 bg-brand-100 dark:bg-brand-900/40 text-brand-800 dark:text-brand-300 rounded text-xs">
                              {file.file_type === 'vendor_po' ? 'PO File' : 'Vendor File'}
                            </span>
                          )}
                        </p>
                        {file.account_name && (
                          <p className="text-xs text-brand-600 dark:text-brand-400 mt-1">
                            Account: {file.account_name}
                          </p>
                        )}
                      </div>
                      <button
                        onClick={() => handleDeleteFile(file.id)}
                        disabled={isDeletingFile === file.id}
                        className="ml-2 px-3 py-1 text-xs font-medium text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/30 rounded-md hover:bg-red-100 dark:hover:bg-red-900/50 disabled:opacity-50"
                      >
                        {isDeletingFile === file.id ? '...' : 'Delete'}
                      </button>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>

          {/* USER MANAGEMENT SECTION */}
          <div className="bg-white dark:bg-gray-800 shadow rounded-lg">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
                <UsersIcon className="h-5 w-5 mr-2" />
                Users
              </h2>
            </div>
            <div className="p-6">
              {isLoadingUsers ? (
                <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-4">Loading users...</p>
              ) : users.length === 0 ? (
                <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-4">No users found</p>
              ) : (
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {[...users]
                    .sort((a, b) => {
                      const accA = (a.account_name || 'zzz').toLowerCase();
                      const accB = (b.account_name || 'zzz').toLowerCase();
                      if (accA !== accB) return accA.localeCompare(accB);
                      return a.username.localeCompare(b.username);
                    })
                    .map((user) => (
                    <div
                      key={user.id}
                      className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-200 dark:border-gray-600"
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center flex-wrap gap-2">
                          <p className="text-sm font-medium text-gray-900 dark:text-white">{user.username}</p>
                          {user.is_super_admin ? (
                            <span className="px-2 py-0.5 text-xs font-medium bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-300 rounded">
                              Super Admin
                            </span>
                          ) : user.is_admin ? (
                            <span className="px-2 py-0.5 text-xs font-medium bg-purple-100 dark:bg-purple-900/40 text-purple-800 dark:text-purple-300 rounded">
                              Admin
                            </span>
                          ) : null}
                          {user.account_name && (
                            <span className="px-2 py-0.5 text-xs font-medium bg-blue-100 dark:bg-blue-900/40 text-blue-800 dark:text-blue-300 rounded">
                              {user.account_name}
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-gray-500 dark:text-gray-400">{user.email}</p>
                      </div>
                      <div className="flex items-center gap-1 ml-2">
                        {/* Gated on the logged-in admin's account feature flag. Non-super-admins
                            are single-tenant so this scopes correctly. Super admins with the flag
                            on their own tenant will see the toggle across all users; that's a
                            known simplification until we expose per-user account settings here. */}
                        {isFeatureEnabled(currentAdmin?.account, 'voucher_requisitions') && (
                          <label
                            className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-md cursor-pointer hover:bg-gray-200 dark:hover:bg-gray-600"
                            title="When on, every invoice this user exports is flagged Needs Approval"
                          >
                            <input
                              type="checkbox"
                              className="h-3 w-3"
                              checked={!!user.requires_approval}
                              onChange={async (e) => {
                                const next = e.target.checked;
                                try {
                                  await OCRApiService.setUserRequiresApproval(user.id, next);
                                  setUsers((prev) =>
                                    prev.map((u) => (u.id === user.id ? { ...u, requires_approval: next } : u)),
                                  );
                                  toast.success(`Requires Approval ${next ? 'on' : 'off'} for ${user.username}`);
                                } catch {
                                  toast.error('Failed to update');
                                }
                              }}
                            />
                            Requires Approval
                          </label>
                        )}
                        <label
                          className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-md"
                          title="Feeds column U (Comp PO Y/N) on Keystone export. Always Yes auto-closes every matched PO line; Always No leaves them open; Per line lets the user pick with a checkbox at edit time. Backed by can_complete_po_line + default_complete_po_yes so KeyNet can sync the permission later."
                        >
                          Complete PO Line:
                          <select
                            className="ml-1 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded px-1 py-0.5 text-xs"
                            value={
                              !user.can_complete_po_line
                                ? 'always_no'
                                : user.default_complete_po_yes
                                ? 'always_yes'
                                : 'per_line'
                            }
                            onChange={async (e) => {
                              const next = e.target.value as 'always_yes' | 'always_no' | 'per_line';
                              const can = next !== 'always_no';
                              const defaultYes = next === 'always_yes';
                              try {
                                await OCRApiService.setUserCompletePoLine(user.id, can, defaultYes);
                                setUsers((prev) =>
                                  prev.map((u) =>
                                    u.id === user.id
                                      ? { ...u, can_complete_po_line: can, default_complete_po_yes: defaultYes }
                                      : u,
                                  ),
                                );
                                toast.success(`Complete PO Line = ${next} for ${user.username}`);
                              } catch {
                                toast.error('Failed to update');
                              }
                            }}
                          >
                            <option value="always_no">Always No</option>
                            <option value="always_yes">Always Yes</option>
                            <option value="per_line">Per line</option>
                          </select>
                        </label>
                        <button
                          onClick={() => setUsageUser({ id: user.id, label: user.username })}
                          className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600"
                          title="View API usage + cost"
                        >
                          <ChartBarIcon className="h-3 w-3" />
                          Usage
                        </button>
                        <button
                          onClick={() => setShowDeleteUserConfirm(user.id)}
                          disabled={isDeletingUser === user.id}
                          className="px-3 py-1 text-xs font-medium text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/30 rounded-md hover:bg-red-100 dark:hover:bg-red-900/50 disabled:opacity-50"
                        >
                          {isDeletingUser === user.id ? '...' : 'Delete'}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Per-user vendor-file assignment UI removed in Phase 2.5: vendor +
            PO CSVs are now scoped to the uploading user's account, and every
            user in that account automatically uses them. */}

        {/* Usage Breakdown Modal (per user) */}
        {usageUser && (
          <UsageBreakdownModal
            mode="user"
            targetId={usageUser.id}
            targetLabel={usageUser.label}
            onClose={() => setUsageUser(null)}
          />
        )}

        {/* Delete User Confirmation Modal */}
        {showDeleteUserConfirm && (
          <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
            <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4 shadow-xl">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Delete User</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                Are you sure? This cannot be undone.
              </p>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setShowDeleteUserConfirm(null)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleDeleteUser(showDeleteUserConfirm)}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminPanel;
